import { getProductPricing, formatProductPrice } from "../utils";

export const normalizeStockQuantity = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const parsedQuantity = Number(value);
  if (!Number.isFinite(parsedQuantity)) return null;
  return Math.max(0, parsedQuantity);
};

export const normalizeExclusiveProducts = (response) => {
  const products = Array.isArray(response?.results) ? response.results : [];

  return products.map((product, index) => {
    const rawVariants = Array.isArray(product?.variants)
      ? product.variants
      : [];

    const variants = rawVariants
      .map((variant) => {
        const pricing = getProductPricing(variant, true);
        const quantity = normalizeStockQuantity(variant?.quantity);
        const imageValue = String(variant?.image || "");
        return {
          ...variant,
          id: Number(variant?.id),
          size: String(variant?.size || "")
            .trim()
            .toUpperCase(),
          color: variant?.color || "",
          original_price: variant?.price ?? "",
          originalPrice: pricing.originalPrice,
          discounted_price: variant?.discounted_price ?? variant?.price ?? "",
          discountedPrice: pricing.discountedPrice,
          discount: variant?.discount ?? variant?.discount_percent ?? 0,
          discountPercent: pricing.discountPercent,
          savings: pricing.savings,
          hasDiscount: pricing.hasDiscount,
          quantity,
          isOutOfStock: quantity !== null && quantity <= 0,
          image:
            imageValue &&
            imageValue !== "null" &&
            !imageValue.endsWith("/null") &&
            !imageValue.includes("/null")
              ? imageValue
              : "",
        };
      })
      .filter((variant) => variant.id && variant.size);
    const uniqueSizes = [
      ...new Set(variants.map((variant) => variant.size).filter(Boolean)),
    ];

    const firstInStockVariant = variants.find(
      (variant) => variant.isOutOfStock !== true,
    );
    const defaultSize = String(
      firstInStockVariant?.size || uniqueSizes[0] || "",
    )
      .trim()
      .toUpperCase();
    const backendSlug = String(product?.slug || "").trim();
    const productPricing = getProductPricing(product, true);
    return {
      id: Number(product?.id),
      displayId: String(index + 1).padStart(2, "0"),
      name: product?.name || "",
      price: formatProductPrice(product?.discounted_price ?? product?.price),
      original_price: product?.price ?? "",
      originalPrice: productPricing.originalPrice,
      discounted_price: product?.discounted_price ?? product?.price ?? "",
      discountedPrice: productPricing.discountedPrice,
      discount: product?.discount ?? product?.discount_percent ?? 0,
      discountPercent: productPricing.discountPercent,
      savings: productPricing.savings,
      hasDiscount: productPricing.hasDiscount,
      image: product?.image || "",
      image_url: product?.image || "",
      sizes: uniqueSizes,
      defaultSize,
      variants,
      quantity: normalizeStockQuantity(product?.quantity),
      category: product?.category || "",
      garmentType: product?.category || "",
      distributor: product?.distributor || "",
      brand: product?.distributor || "",
      color: product?.color || variants[0]?.color || "",
      realSlug: backendSlug,
      slug: backendSlug,
      productType: "exclusive",
      product_type: "exclusive",
      is_marketplace: false,
    };
  });
};

export const getExclusiveVariantForSize = (piece, size) => {
  const normalizedSize = String(size || "")
    .trim()
    .toUpperCase();

  if (!normalizedSize) return null;

  const matchingVariants = (piece?.variants || []).filter(
    (variant) =>
      String(variant?.size || "")
        .trim()
        .toUpperCase() === normalizedSize,
  );

  return (
    matchingVariants.find((variant) => variant?.isOutOfStock !== true) ||
    matchingVariants[0] ||
    null
  );
};

export const hasAvailableExclusiveVariant = (product) => {
  return (product?.variants || []).some((variant) => {
    const quantity = normalizeStockQuantity(variant?.quantity);

    return (
      variant?.id &&
      variant?.size &&
      variant?.isOutOfStock !== true &&
      (quantity === null || quantity > 0)
    );
  });
};

export const getExclusiveVariantPricing = (piece, selectedVariant) => {
  if (!selectedVariant) {
    return getProductPricing(piece, true);
  }

  const hasVariantDiscountedPrice =
    selectedVariant?.discounted_price !== null &&
    selectedVariant?.discounted_price !== undefined &&
    selectedVariant?.discounted_price !== "";

  const hasVariantDiscount =
    selectedVariant?.discount !== null &&
    selectedVariant?.discount !== undefined &&
    selectedVariant?.discount !== "";

  return getProductPricing(
    {
      price:
        selectedVariant?.original_price ??
        selectedVariant?.price ??
        piece?.original_price ??
        piece?.originalPrice,
      original_price:
        selectedVariant?.original_price ??
        selectedVariant?.price ??
        piece?.original_price ??
        piece?.originalPrice,
      discounted_price: hasVariantDiscountedPrice
        ? selectedVariant.discounted_price
        : piece?.discounted_price,
      discount: hasVariantDiscount
        ? selectedVariant.discount
        : (piece?.discount ?? piece?.discountPercent),
    },
    true,
  );
};

export const buildExclusiveCartProduct = (piece, selectedVariant) => {
  if (!piece?.id || !selectedVariant?.id) {
    return null;
  }

  const selectedPricing = getExclusiveVariantPricing(piece, selectedVariant);
  const cartItemId = Number(selectedVariant.id);
  const cartImage = selectedVariant?.image || piece?.image || "";

  return {
    ...piece,
    id: cartItemId,
    product_id: Number(piece.id),
    variant_id: cartItemId,
    image: cartImage,
    image_url: cartImage,
    main_images: [cartImage].filter(Boolean),
    available_quantity: selectedVariant?.quantity,
    price: selectedPricing.discountedPrice,
    original_price: selectedPricing.originalPrice,
    discounted_price: selectedPricing.discountedPrice,
    discount: selectedPricing.discountPercent,
    discount_percent: selectedPricing.discountPercent,
    savings: selectedPricing.savings,
    variant_original_price:
      selectedVariant?.originalPrice ?? selectedPricing.originalPrice,
    variant_discounted_price:
      selectedVariant?.discountedPrice ?? selectedPricing.discountedPrice,
    variant_discount:
      selectedVariant?.discountPercent ?? selectedPricing.discountPercent,
    size: selectedVariant.size,
    color: selectedVariant.color || piece.color || "",
    productType: "exclusive",
    product_type: "exclusive",
    is_marketplace: false,
  };
};
